import React, { useCallback, useEffect, useRef, useState } from "react";
import { withStyles } from "@material-ui/core";
import DropYardCard from "./DropYardCard/index";
import {
  checkErrorInRegion,
  finishDrawing,
  getDropYardSavePayload,
  getDropYardsWithinRegion,
  isDropYardHaveRegion,
} from "../utils";
import drawConstant from "../../../../common/DrawTools/DrawConstant";
import Events from "../../../../../../../utilities/EventEmitter";
import { withSnackbar } from "notistack";
import { compose } from "recompose";
import AlertPopup from "../../../../Terminal/RouteMappingTool/components/common/AlertPopup";
import {
  getDataWithQueryString,
  postData,
} from "../../../../../../../api/componentAPIs";
import Loader from "./Loader";

const styles = (theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minWidth: 360,
    overflowY: "auto",
  },
  header: {
    padding: "8px 12px",
    fontWeight: 600,
    color: theme.HeaderSelectedText,
    borderBottom: `1px solid ${theme.gridGroupHeaderBorderColor}`,
    fontSize: theme.headerTextFontSize,
  },
  dropYardList: {
    display: "flex",
    flexDirection: "column",
    padding: 12,
    gap: "10px",
    flex: 1,
  },
  sectionTitle: {
    textTransform: "uppercase",
    fontSize: theme.textFontSize,
    color: theme.gridTextColor,
    fontWeight: 600,
    marginBottom: 8,
  },
  noDataMessage: {
    flex: 1,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    color: theme.gridTextColor,
    fontSize: theme.textFontSize,
    textAlign: "center",
  },
});

const DropYardsDrawer = ({
  classes,
  dropYardList,
  dropYardClickedOnMap,
  geoDataList,
  enqueueSnackbar,
  CommonStore,
}) => {
  const [activeDropYardId, setActiveDropYardId] = useState(null);
  const [isEditInProgress, setIsEditInProgress] = useState(false);
  const [newDropYard, setNewDropYard] = useState(null);
  const [isDrawingInProgress, setIsDrawingInProgress] = useState(false);
  const [isUpdateInProgress, setIsUpdateInProgress] = useState(false);
  const timeoutRef = useRef(null);
  const [initialBoundary, setInitialBoundary] = useState(null);

  const handleDrawingSelectedOnMap = useCallback(
    (featureIds) => {
      // Handler for when a drawing is selected on the map
      if (isEditInProgress) return;
      const dropYardId = featureIds.length > 0 ? featureIds[0] : null;
      setActiveDropYardId(dropYardId);
      const selectedDropYard = dropYardList?.find(
        (dropYard) => dropYard?.dropYardId === dropYardId
      );
      if (selectedDropYard) {
        dropYardClickedOnMap({ data: selectedDropYard });
      }
    },
    [isEditInProgress, dropYardList, dropYardClickedOnMap]
  );

  const handleDrawingUpdate = useCallback(
    (feature) => {
      // Handler for when a new region is added to the map using the drawing tool

      // When the feature id is not a drop yard id, it means this is a new region added
      const isNewRegion = !dropYardList
        .map((dropYard) => dropYard.dropYardId)
        ?.includes(feature?.id);

      if (isNewRegion) {
        // If edit is in progress, do not allow to create a new drop yard
        // This is to prevent the user from creating a new drop yard while editing an existing one
        if (isEditInProgress) return;

        const dropYardsWithinRegion = getDropYardsWithinRegion(
          feature,
          dropYardList
        );
        const errorMessage = checkErrorInRegion({
          feature,
          dropYardsWithinRegion,
          geoDataList,
        });
        if (errorMessage) {
          handleErrorInRegion(errorMessage, feature);

          // Reset the new drop yard creation process, if the user have made the polygon invalid
          setNewDropYard(null);
          setActiveDropYardId(null);
          setIsEditInProgress(false);
        } else {
          const newDropYard = { ...dropYardsWithinRegion[0] };
          newDropYard.boundary = feature?.geometry?.coordinates[0];
          setNewDropYard(newDropYard);
          dropYardClickedOnMap({ data: newDropYard });
          setActiveDropYardId(newDropYard?.dropYardId);
          setIsEditInProgress(true);
        }
      } else {
        const selectedDropYardId = feature?.id;

        // Store the initial boundary when the user starts editing an existing drop yard
        // So he won't lose the first change he made
        setInitialBoundary(feature?.geometry?.coordinates[0]);
        setActiveDropYardId(selectedDropYardId);
        setIsEditInProgress(true);
      }
    },
    [dropYardList, geoDataList, dropYardClickedOnMap]
  );

  useEffect(() => {
    const handleDrawingInProgress = () => {
      setIsDrawingInProgress(true);
    };

    Events.on(
      drawConstant.ROUTE_MAPPING_EVENTS.DRAWING_IN_PROGRESS,
      handleDrawingInProgress
    );
    return () => {
      Events.off(
        drawConstant.ROUTE_MAPPING_EVENTS.DRAWING_IN_PROGRESS,
        handleDrawingInProgress
      );
    };
  }, [setIsDrawingInProgress]);

  useEffect(() => {
    const { ROUTE_MAPPING_EVENTS } = drawConstant;
    Events.on(
      ROUTE_MAPPING_EVENTS.DRAWING_SELECTED_ON_MAP,
      handleDrawingSelectedOnMap
    );
    return () => {
      Events.off(
        ROUTE_MAPPING_EVENTS.DRAWING_SELECTED_ON_MAP,
        handleDrawingSelectedOnMap
      );
    };
  }, [handleDrawingSelectedOnMap]);

  useEffect(() => {
    const { ROUTE_MAPPING_EVENTS } = drawConstant;
    Events.on(ROUTE_MAPPING_EVENTS.DRAWING_UPDATED, handleDrawingUpdate);
    return () => {
      Events.off(ROUTE_MAPPING_EVENTS.DRAWING_UPDATED, handleDrawingUpdate);
    };
    // Need to add these dependency since the event handler uses it
  }, [handleDrawingUpdate]);

  const handleErrorInRegion = (errorMessage, feature) => {
    enqueueSnackbar(errorMessage, {
      variant: "error",
    });

    // This is a quick fix to prevent the drawing tools icon to be disabled after resetting using this way
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    // Set a new timeout
    timeoutRef.current = setTimeout(() => {
      Events.emit(
        drawConstant.ROUTE_MAPPING_EVENTS.DRAWING_CREATED_ON_MAP,
        "cancel",
        false,
        feature?.id
      );
      timeoutRef.current = null; // Clear ref after execution
    }, 0);
  };

  const handleDeleteRegion = async (dropYard) => {
    if (isEditInProgress) return;
    const { dropYardRegionId, terminalId } = dropYard;
    const payload = {
      dropYardRegionId,
      terminalId,
    };
    setIsUpdateInProgress(true);
    try {
      const response = await postData("DeleteDropYard", payload);
      if (response && response.status >= 200 && response.status < 400) {
        await refreshDropYardData();
        enqueueSnackbar("Drop yard region deleted successfully", {
          variant: "success",
        });
        setIsUpdateInProgress(false);
      } else {
        handleError();
      }
    } catch (error) {
      handleError();
    }
  };

  const handleSaveRegion = async (dropYardFormData) => {
    setIsUpdateInProgress(true);
    setIsEditInProgress(false);
    setActiveDropYardId(null);
    setNewDropYard(null);
    setIsDrawingInProgress(false);
    const payload = getDropYardSavePayload(dropYardFormData);
    try {
      const response = await postData("SaveDropYard", payload);
      if (response && response.status >= 200 && response.status < 400) {
        await refreshDropYardData();
        enqueueSnackbar("Drop yard updated successfully", {
          variant: "success",
        });
        setIsUpdateInProgress(false);
      } else {
        handleError(true);
      }
    } catch (error) {
      handleError(true);
    }

    // Reset the drawing tools for enabling drawing again
    finishDrawing();
  };

  const refreshDropYardData = async () => {
    const response = await getDataWithQueryString("GetAllDropYard");
    if (response?.status === 200) {
      CommonStore.updateDropYardList(response.data);
      return response;
    }

    CommonStore.updateDropYardList([]);
    throw new Error("Failed to fetch drop yard data");
  };

  const handleError = (isSaving) => {
    setIsUpdateInProgress(false);
    const errorMessage = `Error while ${isSaving ? "saving" : "deleting"} data`;
    enqueueSnackbar(errorMessage, {
      variant: "error",
    });
  };

  const cancelNewDropYard = () => {
    setNewDropYard(null);
    setActiveDropYardId(null);
  };

  const resetInitialBoundary = () => {
    setInitialBoundary(null);
  };

  const dropYardWithExistingRegions = dropYardList.filter((dropYard) =>
    isDropYardHaveRegion(dropYard)
  );

  const commonProps = {
    activeDropYardId,
    setActiveDropYardId,
    isEditInProgress,
    setIsEditInProgress,
    dropYardClickedOnMap,
    handleSaveRegion,
    handleDeleteRegion,
  };

  return (
    <div className={`${classes.container} customScroll`}>
      <div className={classes.header}>Mark Drop Yard Region</div>
      {isUpdateInProgress ? (
        <Loader />
      ) : (
        <div className={classes.dropYardList}>
          {newDropYard && (
            <DropYardCard
              key={"newRegion"}
              dropYard={newDropYard}
              isNewDropYard={true}
              cancelNewDropYard={cancelNewDropYard}
              {...commonProps}
            />
          )}
          <div className={classes.sectionTitle}>Existing Regions</div>
          {dropYardWithExistingRegions?.length > 0 ? (
            dropYardWithExistingRegions.map((dropYard) => (
              <DropYardCard
                key={dropYard.dropYardId}
                dropYard={dropYard}
                initialBoundary={initialBoundary}
                resetInitialBoundary={resetInitialBoundary}
                {...commonProps}
              />
            ))
          ) : (
            <div className={classes.noDataMessage}>
              No drop yard regions available.
            </div>
          )}
        </div>
      )}
      {isDrawingInProgress && (
        <AlertPopup
          title="Drawing in progress"
          message="Drawing in progress, please save or cancel to continue."
          onClose={() => setIsDrawingInProgress(false)}
        />
      )}
    </div>
  );
};

export default compose(withStyles(styles), withSnackbar)(DropYardsDrawer);
